import React from 'react';
import { Paper, Box, Divider, Typography } from '@mui/material';
import { IC, IFontSizes, IFontWeights, IRadius, INumericSx } from '../insightsTheme';

/**
 * KpiTicker — dense numeric ribbon below the hero.
 *
 * Each item: { label, value, deltaLabel, spark: number[] }
 * Values are already formatted strings (orchestrator decides locale & currency).
 */
const KpiTicker = ({ items }) => {
  if (!items || items.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: `${IRadius.card}px`,
        borderColor: IC.bdr,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        overflow: 'hidden',
      }}
    >
      {items.map((k, i) => {
        const spark = k.spark && k.spark.length > 1 ? k.spark : [0, 0];
        const sparkMax = Math.max(...spark);
        const sparkMin = Math.min(...spark);
        const range = sparkMax - sparkMin || 1;
        const W = 54;
        const H = 22;
        const pts = spark
          .map((v, si) => {
            const x = (si / (spark.length - 1)) * W;
            const y = H - ((v - sparkMin) / range) * H;
            return `${x},${y}`;
          })
          .join(' ');
        const isUp = k.trend === 'up';
        const isDown = k.trend === 'down';
        const stroke = isUp ? IC.grn : isDown ? IC.red : IC.txM;
        const deltaColor = isUp ? IC.grn : isDown ? IC.red : IC.txM;
        const lastY = parseFloat(pts.split(' ').pop().split(',')[1] || '0');
        return (
          <React.Fragment key={i}>
            {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: IC.bdrL, display: { xs: 'none', sm: 'flex' } }} />}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderTop: i > 0 ? { xs: `1px solid ${IC.bdrL}`, sm: 'none' } : 'none',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: `${IFontSizes.xs}px`,
                    color: IC.txM,
                    fontWeight: IFontWeights.label,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {k.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: `${IFontSizes.lg}px`,
                    fontWeight: IFontWeights.strong,
                    color: IC.tx,
                    ...INumericSx,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {k.value}
                </Typography>
                {k.deltaLabel ? (
                  <Typography
                    sx={{
                      fontSize: `${IFontSizes.xs}px`,
                      color: deltaColor,
                      fontWeight: IFontWeights.label,
                      ...INumericSx,
                    }}
                  >
                    {k.deltaLabel}
                  </Typography>
                ) : null}
              </Box>
              <svg width={W} height={H} style={{ flexShrink: 0 }}>
                <polyline fill="none" stroke={stroke} strokeWidth="1.5" points={pts} />
                <circle cx={W} cy={lastY} r="2" fill={stroke} />
              </svg>
            </Box>
          </React.Fragment>
        );
      })}
    </Paper>
  );
};

export default KpiTicker;
